import { redirectToLoginRoute } from '~/plugins/authorization.client';
import { useRuntimeConfig } from '#imports';
import axios from 'axios';
import type { AxiosResponse, Method } from 'axios';
import PublishButtonStatusUpdate from '~/windowEvents/PublishButtonStatusUpdate';
import User from '~/auth/User';
import GroupHashHelper from '~/helpers/GroupHashHelper';
import Software from "~/models/softwareStrategy/Software";

export abstract class ApiClient {
  private static readonly EDIT_MODE_PARAM = 'em';
  private static readonly GROUP_HASH_PARAM = 'gh';
  private static readonly SOFTWARE_ID_PARAM = 'softwareId';
  private static readonly SOFTWARE_LOCAL_STORAGE_KEY = 'software';

  private static readonly  DOMAIN_REGEX = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

  public constructor() {
    const config = useRuntimeConfig();
    axios.defaults.baseURL = this.buildApiUrl(config.public.apiUrl);
    this.setAuthorizationHeader();
  }

  private buildApiUrl(apiUrl: string): string {
    apiUrl = this.ensureApiUrl(apiUrl);
    if (!this.isValidDomain(apiUrl)) {
      apiUrl = this.appendToOrigin(apiUrl);
    }
    return apiUrl;
  }

  private ensureApiUrl(apiUrl: string): string {
    return apiUrl || '';
  }

  private isValidDomain(url: string): boolean {
    return ApiClient.DOMAIN_REGEX.test(url);
  }

  private appendToOrigin(url: string): string {
    return `${window.location.origin}${url}`;
  }

  private setAuthorizationHeader(): void {
    const token = User.getToken();
    if (token) {
      axios.defaults.headers['Authorization'] = 'Bearer ' + token.toString();
    }
  }

  public async delete(path: string, attributes: object) {
    return await this.request('DELETE', path, attributes);
  }

  public async get<T = any>(
    path: string,
    params: Record<string, any> = {}
  ): Promise<AxiosResponse<T>> {
    return await this.request('GET', path, undefined, params);
  }

  public async patch(path: string, attributes: object) {
    return await this.request('PATCH', path, attributes);
  }

  public async post(path: string, attributes: object = {}) {
    return await this.request('POST', path, attributes);
  }

  protected async request<T = any>(method: Method, url: string, data?: object, params: { [key: string]: any } = {}): Promise<AxiosResponse<T>> {
    try {
      params[ApiClient.EDIT_MODE_PARAM] = useEditMode().value;
      const isGetRequest = method === 'GET';
      const software = localStorage.length ? JSON.parse(localStorage.getItem(ApiClient.SOFTWARE_LOCAL_STORAGE_KEY)) : null;

      if (this.canBeAdded('gh', url, software)) {
        params[ApiClient.GROUP_HASH_PARAM] = this.getGroupHash();
      }

      if (this.canBeAdded('softwareId', url, software)) {
        params[ApiClient.SOFTWARE_ID_PARAM] = software.id;
      }
      const response = await axios({ method, url, data, params });
      const authorizationHeader = response.headers['authorization'];
      authorizationHeader && new User().refreshSession(authorizationHeader.split(' ')[1]);
      !isGetRequest && PublishButtonStatusUpdate.dispatchEvent();

      return response;
    } catch (error: any) {
      if (error.response?.status === 403) {
        new User().removeCookies();
        redirectToLoginRoute();
      }

      throw error;
    }
  }

  protected canBeAdded(paramName: string, url: string, software): boolean {
    switch (paramName) {
      case 'softwareId':
        return software && !url.includes(`${paramName}=`)
      case 'gh':
        return software?.type === Software.AUTO && !!this.getGroupHash() && !url.includes(`?${paramName}=`)
    }
  }

  protected getGroupHash(): string {
    return GroupHashHelper.getGroupHash();
  }
}
